import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import type { Channel, Media } from 'lib-shared-types-channels';
import { Component, Fragment } from 'react';

import { getChannelName } from 'utils/channels';
import { mediaAttributes, MetaWorkingMedia } from 'utils/channels/media';
import { listToAPStyleComma } from 'utils/formatting/string';
import { requestMicrophoneAccess } from 'utils/telephony/microphone';

import type { ProfileProps } from '../containers/profile';

import './../styles/profile.less';

type PropsT = {
  metaMedia: MetaWorkingMedia;
  hasAccess: boolean;
} & Pick<
  ProfileProps,
  | 'presence'
  | 'presenceMeta'
  | 'telephony'
  | 'presenceSetMedia'
  | 'errorMicrophoneErrorShowModal'
  | 'personalToggleRememberMicrophoneAccess'
>;
type StateT = {
  loading: boolean;
};

class MediaChannelBadges extends Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  handlePresenceSetMedia(media: Media): void {
    const { metaMedia } = this.props;
    const working = !metaMedia.working;
    this.setState({ loading: true });

    this.props
      .presenceSetMedia({ media, working })
      .then(() => {
        this.setState({ loading: false });

        ampli.presenceMediaUpdated({
          media,
          enabled: working,
          location: 'topbar',
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  @boundMethod
  onChangeHandler(): void {
    const { hasAccess, metaMedia, telephony } = this.props;
    if (!hasAccess) {
      return;
    }

    if (metaMedia.name === 'speak' && telephony.isMicrophoneBlocked) {
      requestMicrophoneAccess()
        .then(this.props.errorMicrophoneErrorShowModal)
        .catch(this.props.errorMicrophoneErrorShowModal)
        .finally(() => {
          // Remove the remember my microphone access.
          this.props.personalToggleRememberMicrophoneAccess({
            value: false,
          });
        });
    } else {
      this.handlePresenceSetMedia(metaMedia.name);
    }
  }

  getChannelListTextVerb(channels: readonly Channel[]): string {
    if (channels.length === 1) return 'is';
    if (channels.length > 1) return `are`;
    return '';
  }

  getChannelStatusText(
    channels: readonly Channel[],
    statusText: string,
  ): string {
    return channels.length
      ? `${listToAPStyleComma(
          channels.map(getChannelName),
        )} ${this.getChannelListTextVerb(channels)} ${statusText}`
      : '';
  }

  getTooltipText(): string | Array<string | JSX.Element> {
    if (!this.props.hasAccess) {
      return 'Restricted by organization';
    }

    const { metaMedia, presence, telephony } = this.props;
    const { name: mediaName, onChannels, offChannels } = metaMedia;
    const { status } = presence;

    if (mediaName === 'speak') {
      if (telephony.isMicrophoneBlocked) {
        return 'Microphone is blocked. Phone, Callback and voicemail are therefore turned off. Click to enable.';
      }
      if (!telephony.isReady) {
        return telephony.meta.asyncStatus === 'loading'
          ? 'Loading...'
          : 'Phone error';
      }
    }

    const onStatusText =
      status === 'away'
        ? 'inactive because your status is away.'
        : 'turned on.';
    const workingString = this.getChannelStatusText(onChannels, onStatusText);
    const offString = this.getChannelStatusText(offChannels, 'turned off.');

    const interleave = <T,>(arr: string[], thing: T) =>
      ([] as Array<string | T>)
        .concat(...arr.map((n) => [n, thing]))
        .slice(0, -1);

    // Filter out any empty statusText and interleave with breaklines.
    return interleave<JSX.Element>(
      [workingString, offString].filter(Boolean),
      <br />,
    ).map((content, i) => <Fragment key={i}>{content}</Fragment>);
  }

  getChannelAsyncStatus(): AsyncStatus {
    const { metaMedia, presenceMeta } = this.props;
    switch (metaMedia.name) {
      case 'interactivechat':
        return presenceMeta.chatAsyncStatus;
      case 'speak':
        return presenceMeta.voiceAsyncStatus;
      case 'email':
        return presenceMeta.emailAsyncStatus;
      case 'messaging':
        return presenceMeta.messagingAsyncStatus;
      default:
        return 'failed';
    }
  }

  render() {
    const { metaMedia, presence, telephony } = this.props;
    const { status } = presence;
    let statusClassname = 'disabled';

    if (status !== 'offline' && metaMedia.working) {
      if (status === 'working') {
        statusClassname = 'active working';
      } else if (status === 'away') {
        statusClassname = 'active away';
      } else {
        statusClassname = 'active';
      }
    }

    const isTelephony = metaMedia.name === 'speak';
    if (isTelephony) {
      if (telephony.isMicrophoneBlocked) {
        statusClassname = 'error';
      } else if (!telephony.isReady) {
        statusClassname =
          telephony.meta.asyncStatus === 'loading' ? 'loading' : 'error';
      }
    }
    const iconClassName = classnames(
      {
        'profile-presence-icon-big': true,
        'icon-animated':
          this.state.loading === true ||
          this.getChannelAsyncStatus() === 'loading',
      },
      statusClassname,
    );

    return (
      <span className="badge-item">
        <Tooltip content={this.getTooltipText()} wrap="none">
          <span className="badge-btn" onClick={this.onChangeHandler}>
            <Icon
              name={mediaAttributes[metaMedia.name].icon}
              size={24}
              className={iconClassName}
            />
          </span>
        </Tooltip>
      </span>
    );
  }
}

export default MediaChannelBadges;
